import { useQuery } from "@tanstack/react-query";
import type React from "react";
import { useState } from "react";

import type { TicketsSatisfactionProjectDto } from "@/api/types";
import { SegmentGroup } from "@/design-system/SegmentGroup";
import { Skeleton } from "@/design-system/Skeleton";
import { Stack } from "@/design-system/Stack";
import { commonAPIDataSelector } from "@/helpers/Network/selectors";
import { usePortfolioAnalyticsQueries } from "@/queries/portfolio-analytics/queryOptions";
import { useTranslation } from "@/translations";

import { RingChart } from "../Charts/RingChart";
import { FaceSmileIcon } from "../Icons/FaceSmileIcon";
import { AnalyticsWidgetCard } from "./AnalyticsWidgetCard";
import { AnalyticsWidgetProjectList } from "./AnalyticsWidgetProjectList";
import {
  ceilingToOneDecimal,
  derivePrimaryDateRangeFromPeriod,
  deriveSecondaryDateRangeFromPeriod,
  formatAnalyticsDatePayload,
  formatAnalyticsRawValue,
} from "./helpers";
import type { AnalyticsWidgetBaseProps } from "./types";
import { useMetricHelp } from "./useMetricHelp";

type TicketSatisfactionWidgetProps = AnalyticsWidgetBaseProps;

export function TicketSatisfactionWidget({ projectIds = [], period }: TicketSatisfactionWidgetProps): React.ReactNode {
  const [selectedProjectSegment, setSelectedProjectSegment] = useState<"positive" | "negative">("positive");

  const portfolioAnalyticsQueries = usePortfolioAnalyticsQueries();
  const { t } = useTranslation();
  const help = useMetricHelp();

  const secondaryDates = deriveSecondaryDateRangeFromPeriod(period);
  const { data: secondaryData } = useQuery({
    ...portfolioAnalyticsQueries.platformTicketsRatings({
      projectId: projectIds.length === 1 ? projectIds[0] : undefined,
      startDate: formatAnalyticsDatePayload(secondaryDates.startDate),
      endDate: formatAnalyticsDatePayload(secondaryDates.endDate),
    }),
    select: (res) => {
      const data = commonAPIDataSelector(res);

      return {
        ...data,
        projects: data.projects.filter((project) => projectIds.includes(project.projectId)),
      };
    },
  });

  const primaryDates = derivePrimaryDateRangeFromPeriod(period);
  const { data: primaryData, isPending } = useQuery({
    ...portfolioAnalyticsQueries.platformTicketsRatings({
      projectId: projectIds.length === 1 ? projectIds[0] : undefined,
      startDate: primaryDates.startDate ? formatAnalyticsDatePayload(primaryDates.startDate) : undefined,
      endDate: primaryDates.endDate ? formatAnalyticsDatePayload(primaryDates.endDate) : undefined,
    }),
    select: (res) => {
      const data = commonAPIDataSelector(res);

      return {
        ...data,
        projects: data.projects.filter((project) => projectIds.includes(project.projectId)),
      };
    },
  });

  const aggregateData = (projects: TicketsSatisfactionProjectDto[]) => {
    return projects.reduce(
      (acc, project) => {
        // Weighted average calculation
        acc.totalTicketRatings += project.avgSatisfactionScore * project.ticketCount;
        acc.totalRatedTicketCount += project.ticketCount;

        return acc;
      },
      { totalTicketRatings: 0, totalRatedTicketCount: 0 },
    );
  };

  const computeScore = (totalTicketRatings: number, totalRatedTicketCount: number) => {
    if (totalRatedTicketCount === 0) return 0;

    return ceilingToOneDecimal(totalTicketRatings / totalRatedTicketCount);
  };

  const primaryProjects = primaryData?.projects ?? [];
  const primaryAggregatedData = aggregateData(primaryProjects);
  const primaryScore = computeScore(
    primaryAggregatedData.totalTicketRatings,
    primaryAggregatedData.totalRatedTicketCount,
  );

  return (
    <AnalyticsWidgetCard
      title={t("component.analytics-widget.ticket-satisfaction.title")}
      titleTooltip={help("component.analytics-widget.ticket-satisfaction.tooltip")}
      icon={<FaceSmileIcon />}
      isLoading={isPending}
      renderSkeleton={() => <WidgetSkeleton />}
    >
      <section className="flex flex-1 flex-col items-center justify-center gap-space-md text-center">
        <RingChart variant="half" value={primaryScore} max={10} renderValue={formatAnalyticsRawValue} />
        <h4 className="text-heading-4">{t("component.analytics-widget.ticket-satisfaction.subtitle")}</h4>
      </section>
      {primaryData && primaryData.projects.length > 1 && (
        <>
          <SegmentGroup
            value={selectedProjectSegment}
            onChange={(value) => setSelectedProjectSegment(value)}
            items={[
              {
                label: t("component.analytics-widget.project-segment-group.label.top-performing"),
                value: "positive",
              },
              {
                label: t("component.analytics-widget.project-segment-group.label.bottom-performing"),
                value: "negative",
              },
            ]}
          />
          <AnalyticsWidgetProjectList
            order={selectedProjectSegment === "positive" ? "descending" : "ascending"}
            projects={(primaryData?.projects ?? []).map((currPrimaryProject) => {
              const currSecondaryProject = secondaryData?.projects.find(
                (currProject) => currProject.projectId === currPrimaryProject.projectId,
              );

              return {
                projectId: currPrimaryProject.projectId,
                projectName: currPrimaryProject.projectName,
                projectLogo: currPrimaryProject.projectLogo,
                score: currPrimaryProject.avgSatisfactionScore,
                referenceScore: currSecondaryProject ? currSecondaryProject.avgSatisfactionScore : undefined,
              };
            })}
          />
        </>
      )}
    </AnalyticsWidgetCard>
  );
}

function WidgetSkeleton(): React.ReactNode {
  return (
    <Stack gap="md" align="center">
      <Skeleton height="20" width="40" shape="roundedTop" />
      <Skeleton height="6" width="1/2" />
      <Skeleton height="8" />
    </Stack>
  );
}
